/**
 * Manages game state persistence via localStorage.
 * Enables the "Continue" feature from the title screen.
 *
 * Persistence layers:
 *   1. Game save (SAVE_KEY) — current run state, deleted on game over or new game
 *   2. Music preference (MUSIC_PREF_KEY) — persists forever
 *   3. Shop wallet (managed by ShopManager) — persists forever, coins accumulate across runs
 *   4. Shop items (managed by ShopManager) — cleared on game over, preserved on ESC quit
 *   5. Config settings (CONFIG_KEY) — music/SFX volume, master mute, difficulty — persists forever
 */

import {
  DEFAULT_MUSIC_VOLUME_STEP,
  DEFAULT_SFX_VOLUME_STEP,
  DEFAULT_MASTER_MUTE,
  DEFAULT_DIFFICULTY,
} from '../config/game-config.js';

const SAVE_KEY = 'lost-in-labyrinth-save';
const MUSIC_PREF_KEY = 'lost-in-labyrinth-music';
const CONFIG_KEY = 'lost-in-labyrinth-config';
const TUTORIAL_SEEN_KEY = 'lost-in-labyrinth-tutorial-seen';
const TREASURE_ROOM_SEEN_KEY = 'lost-in-labyrinth-treasure-room-seen';

/**
 * Save the current game state to localStorage.
 *
 * @param {object} state
 * @param {number} state.level        - Current maze level
 * @param {number} state.mazeSeed     - Seed used to generate the current maze
 * @param {number} state.mazeCols     - Current maze column count
 * @param {number} state.mazeRows     - Current maze row count
 * @param {number} state.playerCol    - Player grid column
 * @param {number} state.playerRow    - Player grid row
 * @param {number} state.health       - Current health value
 * @param {number} state.lives        - Remaining lives
 * @param {number} state.score         - Current score
 * @param {number} state.coinsCollected - Total coins collected (persists across levels)
 * @param {number[][]} state.visited  - 2D array of visited cell flags (for fog)
 * @param {string[]} [state.playerKeys] - Array of key tier strings the player holds (e.g. ['metallic','gold'])
 * @param {number} [state.agmuthCount] - Number of Agmuth items found this playthrough (0-4)
 * @param {boolean} [state.hasCompass] - Whether the player has collected the compass this level
 * @param {boolean} [state.hasThreiaTotem] - Whether the player currently holds a Threia Totem
 * @param {boolean} [state.threiaBlessingUsed] - Whether the blessing has been used on this level
 * @param {Array<{col:number,row:number,tier:string}>} [state.chests] - Remaining unopened chests in current maze
 * @param {number} [state.hammerCharges] - Combined hammer charges (pickup + shop), 0 = no hammer
 * @param {Array<{row:number,col:number,remaining:number}>} [state.brokenWalls] - Currently broken walls with remaining regen time (ms)
 * @param {number} [state.narratorMonkIndex] - Sequential index into monk message pool (0-based)
 * @param {number} [state.narratorVorathIndex] - Sequential index into Vorath message pool (0-based)
 * @param {number} [state.narratorMonkNextLevel] - Next level the monk is scheduled to appear
 * @param {number} [state.narratorVorathNextLevel] - Next level Vorath is scheduled to appear
 * @param {boolean} [state.hasGoldenExit] - Whether a golden exit exists on the current level
 * @param {{col:number,row:number}} [state.goldenExitPos] - Grid position of the golden exit (if present)
 * @param {boolean} [state.goldenExitSpent] - Whether the golden exit has been used this run
 * @param {string} [state.gameMode] - Game mode ('classic' or 'explorer')
 * @param {number} [state.explorerContinuesUsed] - Number of free continues used in Explorer mode (0-2)
 */
export function saveGame(state) {
  try {
    const serialized = JSON.stringify(state);
    localStorage.setItem(SAVE_KEY, serialized);
  } catch (err) {
    console.warn('Failed to save game state:', err);
  }
}

/**
 * Load a previously saved game state from localStorage.
 *
 * @returns {object|null} The saved state object, or null if none exists.
 */
export function loadGame() {
  try {
    const serialized = localStorage.getItem(SAVE_KEY);
    if (!serialized) {
      return null;
    }
    const data = JSON.parse(serialized);

    // Basic schema validation: ensure critical fields exist and have correct types
    if (
      typeof data !== 'object' || data === null
      || typeof data.level !== 'number'
      || typeof data.mazeSeed !== 'number'
      || typeof data.mazeCols !== 'number'
      || typeof data.mazeRows !== 'number'
      || typeof data.playerCol !== 'number'
      || typeof data.playerRow !== 'number'
      || typeof data.health !== 'number'
      || typeof data.lives !== 'number'
      || !Array.isArray(data.visited)
    ) {
      console.warn('Corrupted save data detected, discarding.');
      localStorage.removeItem(SAVE_KEY);
      return null;
    }

    return data;
  } catch (err) {
    console.warn('Failed to load game state:', err);
    return null;
  }
}

/**
 * Check whether a saved game exists.
 *
 * @returns {boolean}
 */
export function hasSavedGame() {
  const raw = localStorage.getItem(SAVE_KEY);
  if (raw === null) return false;
  try {
    JSON.parse(raw);
    return true;
  } catch (_) {
    return false;
  }
}

/**
 * Delete the saved game from localStorage.
 */
export function deleteSave() {
  localStorage.removeItem(SAVE_KEY);
}

// ---------------------------------------------------------------
// Music preference
// ---------------------------------------------------------------

/**
 * Save the game music preference.
 *
 * @param {boolean} enabled - Whether game music is enabled.
 */
export function saveMusicPref(enabled) {
  try {
    localStorage.setItem(MUSIC_PREF_KEY, JSON.stringify(enabled));
  } catch (err) {
    console.warn('Failed to save music preference:', err);
  }
}

/**
 * Load the game music preference.
 *
 * @returns {boolean} Whether game music is enabled (defaults to true).
 */
export function loadMusicPref() {
  try {
    const val = localStorage.getItem(MUSIC_PREF_KEY);
    if (val === null) return true;
    return JSON.parse(val);
  } catch (err) {
    return true;
  }
}

// ---------------------------------------------------------------
// Configuration settings (volume, mute, difficulty)
// ---------------------------------------------------------------

/**
 * @typedef {object} ConfigSettings
 * @property {number} musicVolumeStep  - Index into VOLUME_STEPS (0-8)
 * @property {number} sfxVolumeStep    - Index into VOLUME_STEPS (0-8)
 * @property {boolean} masterMute      - Whether all audio is muted
 * @property {string} difficulty        - 'easy', 'normal', or 'hard'
 */

/**
 * Save configuration settings to localStorage.
 *
 * @param {ConfigSettings} config
 */
export function saveConfig(config) {
  try {
    localStorage.setItem(CONFIG_KEY, JSON.stringify(config));
  } catch (err) {
    console.warn('Failed to save config:', err);
  }
}

/**
 * Load configuration settings from localStorage.
 *
 * @returns {ConfigSettings} Saved settings or defaults.
 */
export function loadConfig() {
  try {
    const raw = localStorage.getItem(CONFIG_KEY);
    if (!raw) return _defaultConfig();
    const parsed = JSON.parse(raw);
    // Validate and fill in any missing fields with defaults
    return {
      musicVolumeStep: _clampStep(parsed.musicVolumeStep, DEFAULT_MUSIC_VOLUME_STEP),
      sfxVolumeStep: _clampStep(parsed.sfxVolumeStep, DEFAULT_SFX_VOLUME_STEP),
      masterMute: typeof parsed.masterMute === 'boolean' ? parsed.masterMute : DEFAULT_MASTER_MUTE,
      difficulty: _validDifficulty(parsed.difficulty),
    };
  } catch (err) {
    console.warn('Failed to load config:', err);
    return _defaultConfig();
  }
}

/** @returns {ConfigSettings} */
function _defaultConfig() {
  return {
    musicVolumeStep: DEFAULT_MUSIC_VOLUME_STEP,
    sfxVolumeStep: DEFAULT_SFX_VOLUME_STEP,
    masterMute: DEFAULT_MASTER_MUTE,
    difficulty: DEFAULT_DIFFICULTY,
  };
}

/**
 * Clamp a volume step index to 0-8.
 * @param {*} val
 * @param {number} fallback
 * @returns {number}
 */
function _clampStep(val, fallback) {
  if (typeof val !== 'number' || !Number.isFinite(val)) return fallback;
  return Math.max(0, Math.min(8, Math.round(val)));
}

/**
 * Validate difficulty string.
 * @param {*} val
 * @returns {string}
 */
function _validDifficulty(val) {
  const valid = ['easy', 'normal', 'hard'];
  return valid.includes(val) ? val : DEFAULT_DIFFICULTY;
}

// ---------------------------------------------------------------
// First-time tutorial flag
// ---------------------------------------------------------------

/**
 * Mark the first-time tutorial as seen.
 * Once set, the tutorial modal will never be shown again.
 */
export function saveTutorialSeen() {
  try {
    localStorage.setItem(TUTORIAL_SEEN_KEY, '1');
  } catch (err) {
    console.warn('Failed to save tutorial-seen flag:', err);
  }
}

/**
 * Check whether the first-time tutorial has already been shown.
 *
 * @returns {boolean} True if the player has already seen the tutorial.
 */
export function hasTutorialBeenSeen() {
  try {
    return localStorage.getItem(TUTORIAL_SEEN_KEY) === '1';
  } catch (_) {
    return false;
  }
}

// ---------------------------------------------------------------
// Vorath's Treasure Room first-time explanation flag
// ---------------------------------------------------------------

/**
 * Mark the treasure room explanation as seen.
 * Once set, the introductory modal will never be shown again.
 */
export function saveTreasureRoomSeen() {
  try {
    localStorage.setItem(TREASURE_ROOM_SEEN_KEY, '1');
  } catch (err) {
    console.warn('Failed to save treasure-room-seen flag:', err);
  }
}

/**
 * Check whether the player has already seen the treasure room explanation.
 *
 * @returns {boolean} True if the player has already seen the explanation.
 */
export function hasTreasureRoomBeenSeen() {
  try {
    return localStorage.getItem(TREASURE_ROOM_SEEN_KEY) === '1';
  } catch (_) {
    return false;
  }
}
